import type { BaseFields } from "@/resources/base";
import type { Coupon, CouponWithId } from "@/resources/coupon";
import type { Filter } from "@/utils/apiFilter";
import { apiClient } from "./apiClient";

class PackageCouponService {
  async list(
    packageIdOrCode: string,
    filter: Filter = {},
  ): Promise<CouponWithId[]> {
    try {
      const response = await apiClient.get<CouponWithId[]>(
        `/packages/${packageIdOrCode}/coupons`,
        { ...filter, limit: 10000 },
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to list packages:", error);
      return [];
    }
  }
  async createMany(
    packageIdOrCode: string,
    coupons: Omit<Coupon, keyof BaseFields>[],
  ): Promise<CouponWithId[]> {
    try {
      const response = await apiClient.post<CouponWithId[]>(
        `/packages/${packageIdOrCode}/coupons`,
        {
          coupons,
        },
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to create coupons:", error);
      return [];
    }
  }

  async updateByPackageIdAndCouponCode(
    packageId: string,
    couponCode: string,
    couponData: Partial<Coupon>,
  ): Promise<boolean> {
    try {
      const response = await apiClient.put(
        `/packages/${packageId}/coupons/${couponCode}`,
        couponData,
      );
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to update coupon:", error);
      return false;
    }
  }

  async deleteByPackageIdAndCouponCode(
    packageId: string,
    couponCode: string,
  ): Promise<boolean> {
    try {
      const response = await apiClient.delete(
        `/packages/${packageId}/coupons/${couponCode}`,
      );
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to delete coupon:", error);
      return false;
    }
  }

  async resetLearnerPassword(
    packageIdOrCode: string,
    couponCode: string,
    newPassword: string,
  ): Promise<boolean> {
    try {
      const response = await apiClient.post(
        `/packages/${packageIdOrCode}/coupons/${couponCode}/reset-learner-password`,
        { newPassword },
      );
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to reset learner password:", error);
      return false;
    }
  }
}

export const packageCouponService = new PackageCouponService();
